'use client';

import { useState } from 'react';
import { HStack, Box, VStack } from '@terra/ui-styled-system';
import { Badge, Input } from '@terra/ui-design-system';
import { Cancel01Icon } from '@terra/ui-icons';

type TagInputProps = {
  tags: string[];
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  placeholder?: string;
  maxTags?: number;
};

export function TagInput({
  tags,
  onAddTag,
  onRemoveTag,
  placeholder = 'Type tag and press Enter...',
  maxTags = 10,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (tags.length < maxTags) {
        onAddTag(inputValue.trim());
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      onRemoveTag(tags[tags.length - 1]);
    }
  };

  return (
    <VStack gap="2" alignItems="stretch">
      {/* Tags Display */}
      {tags.length > 0 && (
        <HStack gap="1" flexWrap="wrap">
          {tags.map((tag) => (
            <HStack key={tag} gap="0.5" alignItems="center">
              <Badge size="sm" variant="outline">
                {tag}
              </Badge>
              <Box
                cursor="pointer"
                onClick={() => onRemoveTag(tag)}
                _hover={{ opacity: 0.7 }}
              >
                <Cancel01Icon size={10} />
              </Box>
            </HStack>
          ))}
        </HStack>
      )}

      {/* Input */}
      {tags.length < maxTags && (
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="sm"
          fontSize="xs"
        />
      )}
    </VStack>
  );
}
